import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { CEO_PHOTO_URL } from "@/lib/site";
import { MapPin, Handshake, Sparkles } from "lucide-react";

const POINTS = [
  { icon: MapPin, k: "Standort", v: "Konolfingen · Bern, Schweiz" },
  { icon: Handshake, k: "Modell", v: "Ein Ansprechpartner. Kein Outsourcing." },
  { icon: Sparkles, k: "Anspruch", v: "Schweizer Qualität in Design, Marketing, Content." },
];

export default function UeberUns() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "14%"]);

  return (
    <section id="ueber-uns" ref={ref} className="bg-[#FAFAF7] py-24 md:py-36 border-t border-black/5">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid grid-cols-12 gap-6 lg:gap-16 items-start">
        <div className="col-span-12 lg:col-span-6">
          <div className="flex items-center gap-4 mb-8">
            <span className="chapter-num">03 — Über uns</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
          >
            Eine moderne<br />Schweizer <em className="italic text-[#C9A961]">Digitalagentur</em>.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-8 text-[#0A0A0A]/75 text-base md:text-lg leading-relaxed max-w-xl"
          >
            Hudak Enterprises ist eine Digitalagentur aus Bern. Wir verbinden Design, Content und Performance Marketing zu einem System, das für Sie täglich arbeitet. Keine Subunternehmen. Keine Ausreden. Ein Team, ein Ansprechpartner — Schweizer Qualität.
          </motion.p>

          <div className="mt-12 space-y-6">
            {POINTS.map((p, i) => (
              <motion.div
                key={p.k}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.3 + i * 0.08 }}
                className="grid grid-cols-12 gap-4 items-start pb-6 border-b border-black/10"
              >
                <div className="col-span-12 md:col-span-4 flex items-center gap-3">
                  <p.icon size={16} className="text-[#C9A961]" />
                  <span className="font-mono-eyebrow text-[#0A0A0A]/60">{p.k}</span>
                </div>
                <div className="col-span-12 md:col-span-8 font-serif-editorial text-xl md:text-2xl">{p.v}</div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-6 lg:sticky lg:top-28">
          <motion.div
            initial={{ clipPath: "inset(0% 45% 0% 45%)" }}
            animate={inView ? { clipPath: "inset(0% 0% 0% 0%)" } : {}}
            transition={{ duration: 1.4, ease: [0.83, 0, 0.17, 1] }}
            className="relative aspect-[4/5] overflow-hidden bg-[#F0EDE4]"
          >
            <motion.img
              src={CEO_PHOTO_URL}
              alt="Matej Hudak — CEO & Gründer"
              loading="lazy"
              decoding="async"
              style={{ y: imgY }}
              className="w-full h-[115%] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/55 via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 flex items-end justify-between text-[#FAFAF7]">
              <div>
                <div className="font-serif-editorial text-3xl italic">Matej Hudak</div>
                <div className="font-mono-eyebrow text-[#C9A961] mt-2">CEO & Gründer</div>
              </div>
              <div className="font-mono-eyebrow">Bern · CH</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
